import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ExportInbox from '../components/b2b/ExportInbox';
import { parseLeadsCSV } from '../components/b2b/leadCsv';
import TodayView from '../components/b2b/desk/TodayView';
import BoardView from '../components/b2b/desk/BoardView';
import AccountsView from '../components/b2b/desk/AccountsView';
import LeadPage from '../components/b2b/desk/LeadPage';
import { buildToday, istToday } from '../components/b2b/desk/deskModel';
import '../components/b2b/desk/desk.css';

// Swatch kits by buyer niche: picking a niche fills the lead's fabrics (the kit to pack).
export const SWATCH_PACK_TEMPLATES = {
  kaftan_modest: { id: 'kaftan_modest', name: 'Kaftans & modest wear (GCC)', market: 'export', fabrics: ['Fine Modal Satin', 'Bemberg Pure Chiffon', 'Natural Crepe', 'Upada Silk 110', 'Pure Chinon'] },
  domestic_boutique: { id: 'domestic_boutique', name: 'Designer boutique & festive co-ords (India)', market: 'domestic', fabrics: ['Fine Modal Satin', 'Natural Crepe', 'Bemberg Pure Chiffon', 'Upada Silk 110', 'Pure Chinon'] },
  kurti_ethnic: { id: 'kurti_ethnic', name: 'Kurti, daily pret & volume makers (India)', market: 'domestic', fabrics: ['Pure Muslin 110', 'Pure Cotton', 'Poly Georgette', 'Japan Satin', 'Natural Crepe'] },
  resort_boho: { id: 'resort_boho', name: 'Resortwear & boho dresses', market: 'both', fabrics: ['Pure Cotton', 'Pure Muslin 110', 'Flat Chiffon', 'Pure Linen', 'Cotton Chicken Work'] },
  wholesale_souk: { id: 'wholesale_souk', name: 'Wholesale & volume traders', market: 'both', fabrics: ['Poly Georgette', 'Japan Satin', 'BSY Velvet', 'Weightless', 'French Crepe'] },
  festive_bridal: { id: 'festive_bridal', name: 'Festive, occasion & bridal', market: 'both', fabrics: ['Pure Chinon', 'Pure Georgette', 'Pure Organza', 'Dola Silk', 'Velvet'] },
  custom: { id: 'custom', name: 'Custom selection', market: 'both', fabrics: [] },
};

// IDs of old demo rows, excluded from the one-time browser migration.
const DEMO_LEAD_IDS = new Set(['lead-1', 'lead-2', 'lead-3', 'lead-4', 'lead-5', 'lead-6']);
const COUNTRIES = ['India', 'UAE', 'Saudi Arabia', 'Kuwait', 'Qatar', 'UK', 'USA', 'Canada', 'Australia', 'Other'];

function blankLead(market = 'domestic') {
  const niche = market === 'domestic' ? 'domestic_boutique' : 'kaftan_modest';
  return {
    market, brandName: '', contactPerson: '', country: market === 'domestic' ? 'India' : 'UAE', city: '', address: '',
    phone: '', email: '', website: '', instagram: '', niche, fabrics: SWATCH_PACK_TEMPLATES[niche].fabrics,
    shippingModel: 'free', courier: market === 'domestic' ? 'DTDC' : 'Aramex', stage: 'identified', owner: '', notes: '', followUpDate: '',
  };
}

function LeadForm({ initial, team, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };
  return (
    <div className="bd-modal-back" onClick={() => !saving && onCancel()}>
      <form className="bd-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} aria-label={initial.id ? 'Edit lead details' : 'Add lead'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800 }}>{initial.id ? `Edit ${initial.brandName}` : 'Add a lead'}</h2>
          <button type="button" className="bd-btn bd-btn--ghost bd-btn--sm" onClick={onCancel} aria-label="Close">Close</button>
        </div>
        <div className="bd-grid2">
          <label className="bd-field">Brand or studio *<input className="bd-input" required value={form.brandName} onChange={(e) => set({ brandName: e.target.value })} /></label>
          <label className="bd-field">Contact person<input className="bd-input" value={form.contactPerson || ''} onChange={(e) => set({ contactPerson: e.target.value })} /></label>
          <label className="bd-field">Market
            <select className="bd-select" value={form.market || 'domestic'} onChange={(e) => set({ market: e.target.value, courier: e.target.value === 'domestic' ? 'DTDC' : 'Aramex' })}>
              <option value="domestic">India (wholesale@)</option><option value="export">Export (export@)</option>
            </select>
          </label>
          <label className="bd-field">Country
            <select className="bd-select" value={form.country || ''} onChange={(e) => set({ country: e.target.value, market: e.target.value === 'India' ? 'domestic' : 'export' })}>
              <option value="">Unknown</option>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="bd-field">City<input className="bd-input" value={form.city || ''} onChange={(e) => set({ city: e.target.value })} /></label>
          <label className="bd-field">Owner
            <select className="bd-select" value={form.owner || ''} onChange={(e) => set({ owner: e.target.value })}>
              <option value="">Unassigned</option>{team.map((t) => <option key={t.email} value={t.email}>{t.name}</option>)}
            </select>
          </label>
          <label className="bd-field">Email<input type="email" className="bd-input" value={form.email || ''} onChange={(e) => set({ email: e.target.value })} /></label>
          <label className="bd-field">WhatsApp / phone<input className="bd-input" value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} placeholder="+91 98xxx xxxxx" /></label>
          <label className="bd-field">Instagram<input className="bd-input" value={form.instagram || ''} onChange={(e) => set({ instagram: e.target.value })} placeholder="@handle" /></label>
          <label className="bd-field">Website<input className="bd-input" value={form.website || ''} onChange={(e) => set({ website: e.target.value })} /></label>
        </div>
        <label className="bd-field">Studio address (for swatches)<input className="bd-input" value={form.address || ''} onChange={(e) => set({ address: e.target.value })} /></label>
        <div className="bd-grid2">
          <label className="bd-field">Buyer niche (picks the swatch kit)
            <select className="bd-select" value={form.niche || 'custom'} onChange={(e) => set({ niche: e.target.value, fabrics: SWATCH_PACK_TEMPLATES[e.target.value]?.fabrics.length ? SWATCH_PACK_TEMPLATES[e.target.value].fabrics : form.fabrics })}>
              {Object.values(SWATCH_PACK_TEMPLATES).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label className="bd-field">Swatch shipping
            <select className="bd-select" value={form.shippingModel || 'free'} onChange={(e) => set({ shippingModel: e.target.value })}>
              <option value="free">Complimentary (we pay)</option><option value="collect">Buyer pays freight</option>
            </select>
          </label>
        </div>
        <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>Kit: {(form.fabrics || []).join(', ') || 'none yet'}</span>
        <label className="bd-field">Notes<textarea className="bd-textarea" style={{ minHeight: 80 }} value={form.notes || ''} onChange={(e) => set({ notes: e.target.value })} /></label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="bd-btn bd-btn--ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="bd-btn bd-btn--primary" disabled={saving || !form.brandName.trim()}>{saving ? 'Saving…' : initial.id ? 'Save changes' : 'Add lead'}</button>
        </div>
      </form>
    </div>
  );
}

function ImportLeads({ onClose, onImport }) {
  const [raw, setRaw] = useState('');
  const [niche, setNiche] = useState('domestic_boutique');
  const [shipping, setShipping] = useState('free');
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const parse = useMemo(() => parseLeadsCSV(raw, { templates: SWATCH_PACK_TEMPLATES, defaultNiche: niche, defaultShipping: shipping }), [raw, niche, shipping]);
  const readFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => { if (typeof evt.target?.result === 'string') { setRaw(evt.target.result); setSummary(null); } };
    reader.readAsText(file);
    e.target.value = '';
  };
  const run = async () => {
    setSaving(true);
    try { setSummary(await onImport(parse.leads)); setRaw(''); } finally { setSaving(false); }
  };
  return (
    <div className="bd-modal-back" onClick={() => !saving && onClose()}>
      <div className="bd-modal" role="dialog" aria-label="Import leads" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800 }}>Import leads</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--bd-muted)' }}>CSV from Google Maps, Instagram exports or Excel. Duplicates are skipped and listed.</p>
          </div>
          <button type="button" className="bd-btn bd-btn--ghost bd-btn--sm" disabled={saving} onClick={onClose}>{summary ? 'Done' : 'Close'}</button>
        </div>
        {summary && (
          <div style={{ padding: 14, borderRadius: 12, background: 'var(--bd-green-tint)', color: 'var(--bd-green-ink)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong>{summary.created.length} new lead{summary.created.length === 1 ? '' : 's'} added. They wait in Unassigned.</strong>
            {summary.duplicates.length > 0 && <span>{summary.duplicates.length} already in the desk{summary.duplicates.some((d) => d.keptOut) ? ` (${summary.duplicates.filter((d) => d.keptOut).length} of them kept out, left out)` : ''}: {summary.duplicates.slice(0, 6).map((d) => d.brandName).join(', ')}{summary.duplicates.length > 6 ? '…' : ''}</span>}
            {summary.errors.length > 0 && <span style={{ color: 'var(--bd-red)' }}>{summary.errors.length} rows could not be saved: {summary.errors.slice(0, 4).map((e) => `row ${e.row} (${e.message})`).join(', ')}</span>}
          </div>
        )}
        <div className="bd-grid2">
          <label className="bd-field">Default niche
            <select className="bd-select" value={niche} onChange={(e) => setNiche(e.target.value)}>{Object.values(SWATCH_PACK_TEMPLATES).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          </label>
          <label className="bd-field">Swatch shipping
            <select className="bd-select" value={shipping} onChange={(e) => setShipping(e.target.value)}><option value="free">Complimentary</option><option value="collect">Buyer pays</option></select>
          </label>
        </div>
        <label className="bd-btn bd-btn--ghost" style={{ alignSelf: 'flex-start' }}>Choose CSV file<input type="file" accept=".csv,text/csv,text/plain" onChange={readFile} style={{ display: 'none' }} /></label>
        <label className="bd-field">Or paste rows<textarea className="bd-textarea" style={{ minHeight: 120 }} value={raw} onChange={(e) => { setRaw(e.target.value); setSummary(null); }} placeholder="brandName,contactPerson,country,city,phone,email,instagram" /></label>
        {parse.problem && raw.trim() && <span style={{ color: 'var(--bd-red)', fontSize: 13 }}>{parse.problem}</span>}
        {parse.leads.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            <span style={{ color: 'var(--bd-muted)' }}>Columns used: {Object.entries(parse.columns).map(([f, h]) => `${f} ← "${h}"`).join(' · ')}</span>
            <span>{parse.leads.slice(0, 5).map((l) => `${l.brandName} (${l.city || l.country || '?'})`).join(', ')}{parse.leads.length > 5 ? ` and ${parse.leads.length - 5} more` : ''}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="bd-btn bd-btn--primary" disabled={!parse.leads.length || saving} onClick={run}>{saving ? 'Importing…' : `Import ${parse.leads.length} leads`}</button>
        </div>
      </div>
    </div>
  );
}

export default function B2BExportPage() {
  const { currentUser } = useAuth();
  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
  const getToken = useCallback(async () => (currentUser ? currentUser.getIdToken() : ''), [currentUser]);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [team, setTeam] = useState([]);
  const [me, setMe] = useState(null);
  const [view, setView] = useState('today');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [openTool, setOpenTool] = useState('');
  const [inboxThread, setInboxThread] = useState(null);
  const [form, setForm] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState('');
  const today = istToday();

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const notify = useCallback((message) => setToast(message), []);

  const api = useCallback(async (path, method = 'GET', body) => {
    const token = await getToken();
    const response = await fetch(`${apiBase}/api/v1/b2b-export${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.message || `Request failed (${response.status})`);
    return result;
  }, [apiBase, getToken]);

  const reloadLeads = useCallback(async () => {
    const result = await api('/leads');
    setLeads(result.leads || []);
    setError('');
  }, [api]);

  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // One-time transfer of leads that older versions kept only in this browser.
        const raw = localStorage.getItem('fvs_b2b_export_leads');
        const legacy = raw ? JSON.parse(raw) : [];
        if (Array.isArray(legacy) && legacy.length) {
          for (const lead of legacy.filter((item) => item && !DEMO_LEAD_IDS.has(item.id) && !String(item.id || '').startsWith('lead-imp-demo'))) {
            await api('/leads', 'POST', lead);
          }
        }
        if (raw) localStorage.removeItem('fvs_b2b_export_leads');
      } catch (err) {
        if (!cancelled) setError(`Browser lead transfer needs attention: ${err.message}`);
      }
      try {
        const [teamResult] = await Promise.all([api('/team').catch(() => null), reloadLeads()]);
        if (!cancelled && teamResult) {
          setTeam(teamResult.team || []);
          setMe(teamResult.me || null);
          // Reps start on their own work; admins on the whole desk.
          if (teamResult.me && !teamResult.me.isAdmin) setOwnerFilter(teamResult.me.email);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, api, reloadLeads]);

  const saveLead = useCallback(async (lead, patch) => {
    try {
      const { id, ...rest } = { ...lead, ...patch };
      const result = await api(`/leads/${encodeURIComponent(id)}`, 'PUT', rest);
      setLeads((list) => list.map((l) => (l.id === id ? { ...l, ...result.lead, id } : l)));
      return result.lead;
    } catch (err) {
      notify(`Not saved: ${err.message}`);
      throw err;
    }
  }, [api, notify]);

  const loadActivity = useCallback((id) => api(`/leads/${encodeURIComponent(id)}/activity`).then((r) => r.activity || []), [api]);
  const addActivity = useCallback(async (id, type, summary) => {
    try { await api(`/leads/${encodeURIComponent(id)}/activity`, 'POST', { type, summary }); } catch (err) { notify(`Not saved: ${err.message}`); }
  }, [api, notify]);

  const sendEmail = useCallback(async (lead, payload) => {
    try {
      const result = await api(`/leads/${encodeURIComponent(lead.id)}/email`, 'POST', payload);
      await reloadLeads();
      if (result.recordWarning) setError(result.recordWarning);
      return result;
    } catch (err) {
      notify(`Email not sent: ${err.message}`);
      throw err;
    }
  }, [api, reloadLeads, notify]);

  const openLead = (lead, tool = '') => { setOpenId(lead.id); setOpenTool(tool); };
  const openInbox = (threadId) => { setInboxThread(threadId || null); setOpenId(null); setView('inbox'); };
  const onAction = (lead, tool) => {
    if (tool === 'reply' && lead.linkedThreadId) { openInbox(lead.linkedThreadId); return; }
    if (tool === 'wake') { saveLead(lead, { snoozedUntil: '', followUpDate: today }).then(() => notify('Woken up')).catch(() => {}); return; }
    openLead(lead, tool);
  };
  const quickSnooze = (lead) => {
    const date = istToday(new Date(Date.now() + 7 * 86400000));
    saveLead(lead, { snoozedUntil: date, followUpDate: date }).then(() => notify(`${lead.brandName} snoozed until ${date}`)).catch(() => {});
  };
  const moveStage = (lead, stage) => {
    if (stage === 'swatch_delivered' && !lead.awb) { openLead(lead, 'swatch'); notify('Enter the AWB first'); return; }
    saveLead(lead, { stage }).then(() => notify(`${lead.brandName} moved`)).catch(() => {});
  };

  const bulk = async (ids, patch) => {
    try {
      await api('/leads/bulk', 'POST', { ids, patch });
      await reloadLeads();
      notify(patch.owner ? `${ids.length} leads assigned` : `${ids.length} leads snoozed`);
    } catch (err) { notify(`Not saved: ${err.message}`); }
  };

  const keepOut = async (ids, reason, note = '') => {
    try {
      await api('/leads/keep-out', 'POST', { ids, reason, note });
      await reloadLeads();
      notify(ids.length === 1 ? 'Kept out of the desk for good' : `${ids.length} leads kept out of the desk`);
    } catch (err) { notify(`Not saved: ${err.message}`); }
  };
  const bringBack = async (ids) => {
    try {
      await api('/leads/bring-back', 'POST', { ids });
      await reloadLeads();
      notify(ids.length === 1 ? 'Back in the desk, due today' : `${ids.length} leads back in the desk`);
    } catch (err) { notify(`Not saved: ${err.message}`); }
  };

  const syncAccounts = async () => {
    try {
      setSyncing(true);
      const { summary } = await api('/accounts/sync', 'POST');
      await reloadLeads();
      notify(`Synced ${summary.companies} website companies and ${summary.zohoBuyers || 0} Zoho buyers: ${summary.created} new`);
    } catch (err) { setError(err.message); } finally { setSyncing(false); }
  };

  const saveForm = async (data) => {
    try {
      if (data.id) {
        await saveLead(leads.find((l) => l.id === data.id) || data, data);
        notify('Details saved');
      } else {
        const result = await api('/leads', 'POST', data);
        await reloadLeads();
        notify(result.lead.keptOut ? 'This buyer is kept out of the desk: opened it so you can bring it back' : result.lead.duplicate ? 'Already in the desk: opened the existing lead' : 'Lead added');
        setOpenId(result.lead.id);
      }
      setForm(null);
    } catch (err) { notify(`Not saved: ${err.message}`); }
  };

  const importLeads = async (rows) => {
    const result = await api('/leads/import', 'POST', { leads: rows });
    await reloadLeads();
    return result;
  };

  const handleAddToPipeline = async (data) => {
    try {
      const domestic = data.market === 'domestic' || data.country === 'India';
      const result = await api('/leads', 'POST', { ...blankLead(domestic ? 'domestic' : 'export'), ...data, stage: 'replied', followUpDate: today });
      await reloadLeads();
      notify(result.lead.duplicate ? 'Already in the desk' : 'Added to the desk');
      return true;
    } catch (err) { notify(`Not added: ${err.message}`); return false; }
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const digits = q.replace(/\D/g, '');
    return leads.filter((l) => [l.brandName, l.contactPerson, l.city, l.email, l.instagram].some((v) => String(v || '').toLowerCase().includes(q))
      || (digits.length >= 4 && String(l.phone || '').replace(/\D/g, '').includes(digits)));
  }, [leads, search]);

  const todayCount = useMemo(() => buildToday(leads, { today, ownerFilter, me }).total, [leads, today, ownerFilter, me]);
  const openLeadRecord = leads.find((l) => l.id === openId);

  const goView = (next) => { setView(next); setOpenId(null); setSearch(''); if (next !== 'inbox') setInboxThread(null); };

  return (
    <div className="bd">
      <header className="bd-top">
        <span className="bd-brand">B2B Desk</span>
        <nav className="bd-nav" aria-label="Views">
          <button type="button" aria-current={view === 'today' && !openId ? 'page' : undefined} onClick={() => goView('today')}>Today <span className="bd-mono" style={{ fontSize: 12 }}>{todayCount}</span></button>
          <button type="button" aria-current={view === 'board' && !openId ? 'page' : undefined} onClick={() => goView('board')}>Board</button>
          <button type="button" aria-current={view === 'accounts' && !openId ? 'page' : undefined} onClick={() => goView('accounts')}>Accounts</button>
          <button type="button" aria-current={view === 'inbox' && !openId ? 'page' : undefined} onClick={() => goView('inbox')}>Inbox</button>
        </nav>
        <label className="bd-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input aria-label="Search leads" placeholder="Search brand, phone, city, email" value={search} onChange={(e) => { setSearch(e.target.value); if (e.target.value) { setView('today'); setOpenId(null); } }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="bd-k">Showing</span>
          <select className="bd-select" style={{ width: 'auto' }} aria-label="Show work for" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
            <option value="all">Everyone</option>
            {me && <option value={me.email}>Me</option>}
            <option value="unassigned">Unassigned</option>
            {team.filter((t) => t.email !== me?.email).map((t) => <option key={t.email} value={t.email}>{t.name}</option>)}
          </select>
        </label>
        <button type="button" className="bd-btn bd-btn--ghost" onClick={() => setImporting(true)}>Import CSV</button>
        <button type="button" className="bd-btn bd-btn--primary" onClick={() => setForm(blankLead('domestic'))}>Add lead</button>
      </header>

      {loading && <div className="bd-banner" style={{ background: 'var(--bd-indigo-tint)', color: 'var(--bd-indigo-deep)' }} role="status">Loading the desk…</div>}
      {error && <div className="bd-banner" role="alert">{error}<button type="button" className="bd-btn bd-btn--ghost bd-btn--sm" onClick={() => reloadLeads().catch((err) => setError(err.message))}>Retry</button></div>}

      {openLeadRecord ? (
        <LeadPage
          key={openLeadRecord.id}
          lead={openLeadRecord}
          today={today}
          team={team}
          initialTool={openTool}
          onBack={() => setOpenId(null)}
          saveLead={saveLead}
          sendEmail={sendEmail}
          loadActivity={loadActivity}
          addActivity={addActivity}
          openInbox={openInbox}
          onEditDetails={() => setForm({ ...openLeadRecord })}
          onKeepOut={keepOut}
          onBringBack={bringBack}
          notify={notify}
        />
      ) : view === 'today' ? (
        <TodayView leads={leads} today={today} team={team} me={me} ownerFilter={ownerFilter} onOwnerFilter={setOwnerFilter} loadActivity={loadActivity} onOpen={(l) => openLead(l)} onAction={onAction} onQuickSnooze={quickSnooze} searchResults={searchResults} />
      ) : view === 'board' ? (
        <BoardView leads={ownerFilter === 'all' ? leads : leads.filter((l) => (ownerFilter === 'unassigned' ? !l.owner : l.owner === ownerFilter))} today={today} team={team} onOpen={(l) => openLead(l)} onMove={moveStage} />
      ) : view === 'accounts' ? (
        <AccountsView leads={leads} team={team} today={today} onOpen={(l) => openLead(l)} onBulk={bulk} onKeepOut={keepOut} onBringBack={bringBack} onSync={syncAccounts} syncing={syncing} />
      ) : (
        <div style={{ padding: '20px 28px' }}>
          <ExportInbox key={inboxThread || 'inbox'} apiBase={apiBase} getToken={getToken} onAddToPipeline={handleAddToPipeline} activeMarket="all" focusThreadId={inboxThread} />
        </div>
      )}

      {form && <LeadForm initial={form} team={team} onCancel={() => setForm(null)} onSave={saveForm} />}
      {importing && <ImportLeads onClose={() => setImporting(false)} onImport={importLeads} />}
      {toast && <div className="bd-toast" role="status">{toast}</div>}
    </div>
  );
}
